import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationSchema from "_shared/_api/queries/pagination/_schema/pagination-parameters-schema";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import {
  WorkAgreementFilterParameters,
  WorkAgreementOrderParameters,
} from "work/work-agreements/_api/queries/work-agreement-query";
import z from "zod";
import { WorkAgreementOrderField } from "../_fields/work-agreement-order-field";

export type WorkAgreementFilterParametersJSON = z.infer<
  typeof WorkAgreementFilterParametersSchema.WorkAgreementFilterParametersJSONZ
>;
export type WorkAgreementFilterParametersParams = z.infer<
  typeof WorkAgreementFilterParametersSchema.WorkAgreementFilterParametersZ
>;

export type WorkAgreementOrderParametersJSON = z.infer<
  typeof WorkAgreementOrderParametersSchema.WorkAgreementOrderParametersJSONZ
>;
export type WorkAgreementOrderParametersParams = z.infer<
  typeof WorkAgreementOrderParametersSchema.WorkAgreementOrderParametersJSONZ
>;

export type WorkAgreementQueryJSON = z.infer<typeof WorkAgreementQuerySchema.WorkAgreementQueryJSONZ>;
export type WorkAgreementQueryParams = z.infer<typeof WorkAgreementQuerySchema.WorkAgreementQueryZ>;

export class WorkAgreementFilterParametersSchema {
  public static readonly type = z.custom<WorkAgreementFilterParameters>(
    (data) => data instanceof WorkAgreementFilterParameters,
    "Not a valid WorkAgreementFilterParameters instance"
  );

  public static readonly WorkAgreementFilterParametersJSONZ = z.object({
    query: z.string().optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    vendorEntityId: z.uuid().optional(),
    isArchived: z.boolean().optional(),
  });

  public static readonly WorkAgreementFilterParametersZ = z.object({
    query: z.string().optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    vendorEntityId: z.instanceof(Guid).optional(),
    isArchived: z.boolean().optional(),
  });

  public static readonly codec = z.codec(
    WorkAgreementFilterParametersSchema.WorkAgreementFilterParametersJSONZ,
    WorkAgreementFilterParametersSchema.WorkAgreementFilterParametersZ,
    {
      decode: (data) => ({
        query: data.query,
        name: data.name,
        description: data.description,
        vendorEntityId: Guid.fromJSON(data.vendorEntityId) ?? undefined,
        isArchived: data.isArchived,
      }),
      encode: (params) => ({
        query: params.query,
        name: params.name,
        description: params.description,
        vendorEntityId: params.vendorEntityId?.toJSON(),
        isArchived: params.isArchived,
      }),
    }
  );

  public static validate(values: unknown): WorkAgreementFilterParametersParams {
    return parseOrThrow(WorkAgreementFilterParametersSchema.WorkAgreementFilterParametersZ, values, {
      logError: true,
      schemaName: "WorkAgreementFilterParametersSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static deserialize(json: unknown): WorkAgreementFilterParametersParams | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(WorkAgreementFilterParametersSchema.WorkAgreementFilterParametersJSONZ, json, {
      logError: true,
      schemaName: "WorkAgreementFilterParametersSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return this.codec.decode(parsed);
  }

  public static serialize(params: WorkAgreementFilterParametersParams): WorkAgreementFilterParametersJSON {
    const parsed = WorkAgreementFilterParametersSchema.validate(params);
    return this.codec.encode(parsed);
  }
}

export class WorkAgreementOrderParametersSchema {
  public static readonly type = z.custom<WorkAgreementOrderParameters>(
    (data) => data instanceof WorkAgreementOrderParameters,
    "Not a valid WorkAgreementOrderParameters instance"
  );
  public static readonly WorkAgreementOrderParametersJSONZ = z.object({
    orderBy: z.enum(WorkAgreementOrderField),
    orderDirection: z.enum(OrderDirection),
  });
  public static readonly WorkAgreementOrderParametersZ = z.object({
    orderBy: z.enum(WorkAgreementOrderField),
    orderDirection: z.enum(OrderDirection),
  });

  public static readonly codec = z.codec(
    WorkAgreementOrderParametersSchema.WorkAgreementOrderParametersJSONZ,
    WorkAgreementOrderParametersSchema.WorkAgreementOrderParametersZ,
    {
      decode: (data) => ({
        orderBy: data.orderBy,
        orderDirection: data.orderDirection,
      }),
      encode: (params) => ({
        orderBy: params.orderBy,
        orderDirection: params.orderDirection,
      }),
    }
  );

  public static validate(values: unknown): WorkAgreementOrderParametersParams {
    return parseOrThrow(WorkAgreementOrderParametersSchema.WorkAgreementOrderParametersJSONZ, values, {
      logError: true,
      schemaName: "WorkAgreementOrderParametersSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static deserialize(json: unknown): WorkAgreementOrderParametersParams | null | undefined {
    const parsed = parseOrThrow(WorkAgreementOrderParametersSchema.WorkAgreementOrderParametersJSONZ, json, {
      logError: true,
      schemaName: "WorkAgreementOrderParametersSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return WorkAgreementOrderParametersSchema.codec.decode(parsed);
  }

  public static serialize(params: WorkAgreementOrderParametersParams): WorkAgreementOrderParametersJSON {
    const parsed = WorkAgreementOrderParametersSchema.validate(params);
    return parsed;
  }
}

export default class WorkAgreementQuerySchema {
  public static readonly WorkAgreementQueryJSONZ = z.object({
    filterParameters: WorkAgreementFilterParametersSchema.WorkAgreementFilterParametersJSONZ.optional(),
    orderParameters: WorkAgreementOrderParametersSchema.WorkAgreementOrderParametersJSONZ.optional(),
    pagination: PaginationSchema.PaginationJSONZ.optional(),
  });

  public static readonly WorkAgreementQueryZ = z.object({
    get filterParameters() {
      return z.instanceof(WorkAgreementFilterParameters).optional();
    },
    get orderParameters() {
      return z.instanceof(WorkAgreementOrderParameters).optional();
    },
    get paginationParameters() {
      return z.instanceof(PaginationParameters).optional();
    },
  });

  public static readonly codec = z.codec(
    WorkAgreementQuerySchema.WorkAgreementQueryJSONZ,
    WorkAgreementQuerySchema.WorkAgreementQueryZ,
    {
      decode: (data) => ({
        filterParameters: WorkAgreementFilterParameters.fromJSON(data.filterParameters) ?? undefined,
        orderParameters: WorkAgreementOrderParameters.fromJSON(data.orderParameters) ?? undefined,
        paginationParameters: PaginationParameters.fromJSON(data.pagination),
      }),
      encode: (params) => ({
        filterParameters: params.filterParameters ? params.filterParameters.toJSON() : undefined,
        orderParameters: params.orderParameters ? params.orderParameters.toJSON() : undefined,
        pagination: params.paginationParameters ? params.paginationParameters.toJSON() : undefined,
      }),
    }
  );

  public static validate(values: unknown): WorkAgreementQueryParams {
    return parseOrThrow(WorkAgreementQuerySchema.WorkAgreementQueryZ, values, {
      logError: true,
      schemaName: "WorkAgreementQuerySchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): WorkAgreementQueryJSON {
    return parseOrThrow(WorkAgreementQuerySchema.WorkAgreementQueryJSONZ, json, {
      logError: true,
      schemaName: "WorkAgreementQuerySchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is WorkAgreementQueryParams {
    try {
      WorkAgreementQuerySchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is WorkAgreementQueryJSON {
    try {
      WorkAgreementQuerySchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: unknown): WorkAgreementQueryParams | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(WorkAgreementQuerySchema.WorkAgreementQueryJSONZ, json, {
      logError: true,
      schemaName: "WorkAgreementQuerySchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return WorkAgreementQuerySchema.codec.decode(parsed);
  }

  public static serialize(params: WorkAgreementQueryParams): WorkAgreementQueryJSON {
    if (params === null || params === undefined) return params as WorkAgreementQueryJSON;
    const parsed = WorkAgreementQuerySchema.validate(params);
    if (parsed === null || parsed === undefined) return parsed as WorkAgreementQueryJSON;
    return WorkAgreementQuerySchema.codec.encode(parsed);
  }
}
